import { Component, Inject, Injector, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import {
    FormComponent,
    IFormDialogData,
} from 'src/app/shared/form/form.component';
import {
    HierarchicalSelectionFormField,
    NumberFormField,
    SingleSelectionFormField,
    TextFormField,
} from 'src/app/shared/form/formField.model';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ILookup } from 'src/app/shared/form/lookup.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { OrgLevelTypeEnum } from 'src/app/enums/orgLevelType.enum';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { SamplingPlan } from 'src/app/models/samplingPlan.model';
import { RiskAssessment } from 'src/app/models/risk-assessment.model';
import { AssessmentStatusEnum } from 'src/app/enums/assessmentStatus.enum';
import { SamplingPlanStatus } from 'src/app/enums/samplingPlanStatus.enum';
import { SamplingPlanService } from 'src/app/services/samplingPlan.service';
import { RiskAssessmentService } from 'src/app/services/risk-assessment.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';

// DASH-2690 (G11v2): create/edit the parent Sampling Plan. Captures Site + editable Name + Plan Year + an optional
// Reference Risk Assessment (drives the cell bands on the plan detail matrix).
// DASH-2718 item 6: the RA picker is now SITE-SCOPED — it lists only RAs whose organizationLevelId matches the
// chosen Site (mirroring the OperatorTypeEnum filterBy the dialog already builds for org levels) and reloads
// reactively whenever Site changes, instead of loading every RA in the tenant.
// DASH-2718 item 7: the picker now reads FULL RA records (GetList, not GetAll's id/name-only Lookup[]) so it can
// (a) default the selection to the chosen site's Current RA (RiskAssessment.current) and (b) surface name / period /
// status in each option's label. The default is applied only when nothing is pinned yet — on create and whenever the
// user actively changes Site — never on the initial edit load (which must respect the plan's existing pin, or a
// deliberately-empty one). RA stays optional, so a null selection remains a valid resting state.
@Component({
  standalone: false,
    selector: 'app-sampling-plan-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './../../shared/form/form.component.scss',
        './sampling-plan-dialog.component.scss',
    ],
})
export class SamplingPlanDialogComponent
    extends FormComponent<SamplingPlan>
    implements OnInit, OnDestroy
{
    // Site-scoped RA lists are small; a single large page mirrors the risk-register's GetList read (page 0, 10000)
    // and avoids paging the picker.
    private static readonly RA_PAGE_SIZE = 10000;

    organizationLevels: OrganizationLevel[] = [];
    riskAssessments: ILookup[] = [];

    // DASH-2718 item 6/7: the Site picker feeds site ids into this Subject; the ngOnInit pipeline switchMaps each
    // one into a scoped RA fetch so a slow earlier request can never overwrite a newer site's options (the FE
    // picker is the only same-site guard — the BE only checks RA access, not site membership). `applyCurrentDefault`
    // carries whether this change should default the selection to the site's Current RA (item 7): true on create and
    // on a user Site change, false on the initial edit load so an existing (or deliberately-empty) pin is preserved.
    private readonly siteChange$ = new Subject<{ siteId: string | null; applyCurrentDefault: boolean }>();
    private readonly destroy$ = new Subject<void>();

    constructor(
        dialogRef: MatDialogRef<SamplingPlanDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        samplingPlanService: SamplingPlanService,
        private riskAssessmentService: RiskAssessmentService,
        private organizationLevelService: OrganizationLevelService,
        protected injector: Injector,
    ) {
        super(dialogRef, samplingPlanService, data, 'Sampling Plan', injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        this.initSiteScopePipeline();
        const orgPayload = JSON.stringify({
            OperatorTypeEnum: JSON.stringify({
                Condition: OperatorTypeEnum.Equal,
                Field: nameOf<OrganizationLevel>(x => x.orgLevelType),
                Value: `${OrgLevelTypeEnum.Site},${OrgLevelTypeEnum.Function},${OrgLevelTypeEnum.BusinessUnit},${OrgLevelTypeEnum.Corporate}`,
            }),
        });
        forkJoin({
            organizationLevels: this.organizationLevelService.getAll(orgPayload),
            entity: this.getEntity(),
        }).subscribe({
            next: (result) => {
                this.organizationLevels = result.organizationLevels;
                this.entity = result.entity;
                this.initFields();
                // On edit the plan already has a Site, so load its scoped RAs (but keep its existing pin — no
                // current-default); on create there is no Site yet, so the picker starts empty and fills — and
                // defaults to the site's Current RA — once a Site is chosen (see onSiteChange).
                this.siteChange$.next({
                    siteId: this.entity?.organizationLevelId ?? null,
                    applyCurrentDefault: this.isAdd,
                });
                this.isFormLoading = false;
            },
            error: (error) => this.handleLoadError(error),
        });
    }

    // DASH-2706 AC1 (create-flow lands in the matrix): on a create, resolve the dialog with the new plan id so the
    // list can navigate straight into planning/:id. Edit/cancel keep the existing boolean close contract.
    override closeDialog(data: unknown = null): void {
        const created = data as SamplingPlan | null;
        const newId = this.isAdd && created && typeof created.id === 'string' && created.id ? created.id : null;
        this.dialogRef.close(newId ?? true);
    }

    getNewObject(): SamplingPlan {
        return {
            name: null,
            year: new Date().getFullYear(),
            status: SamplingPlanStatus.Draft,
            organizationLevelId: null,
            referenceRiskAssessmentId: null,
        };
    }

    validate(): boolean {
        return true;
    }

    private initFields(): void {
        const currentYear = new Date().getFullYear();
        this.fields = [
            new HierarchicalSelectionFormField({
                property: nameOf<SamplingPlan>(x => x.organizationLevelId),
                name: 'Site',
                required: true,
                options: toOrganizationTree(this.organizationLevels),
                columnSpan: new ColumnSpan(1, 2),
                // DASH-2718 item 6: reload the (site-scoped) RA options whenever the Site changes. ngModel has
                // already written the new id to entity.organizationLevelId by the time this fires.
                onChange: () => this.onSiteChange(),
            }),
            new TextFormField({
                property: nameOf<SamplingPlan>(x => x.name),
                name: 'Name',
                required: true,
                columnSpan: new ColumnSpan(1, 2),
            }),
            new NumberFormField({
                property: nameOf<SamplingPlan>(x => x.year),
                name: 'Plan Year',
                required: true,
                // DASH-2676/DASH-2690: a plan Year is exactly 4 digits and the current year or later (sampling is
                // forward-looking). Mirrors the BE SamplingPlanService year guard.
                min: currentYear,
                max: 9999,
                validator: (value: unknown) => this.validateYear(value),
                columnSpan: new ColumnSpan(1, 2),
            }),
            new SingleSelectionFormField({
                property: nameOf<SamplingPlan>(x => x.referenceRiskAssessmentId),
                name: 'Reference Risk Assessment',
                required: false,
                options: this.riskAssessments,
                searchKeys: [nameOf<ILookup>(x => x.name)],
                columnSpan: new ColumnSpan(1, 2),
            }),
        ];
    }

    // Wires the Site → scoped-RA reload as a single long-lived subscription. switchMap cancels any in-flight fetch
    // when a newer Site arrives, so a slow earlier request can never overwrite the current site's options; takeUntil
    // tears it down in ngOnDestroy (a dialog closed mid-flight no longer runs its callback). This is the reactive
    // shape components.md mandates — replaces the previous per-call nested .subscribe().
    private initSiteScopePipeline(): void {
        this.siteChange$
            .pipe(
                switchMap(({ siteId, applyCurrentDefault }) =>
                    this.loadScopedRiskAssessments(siteId).pipe(
                        map(riskAssessments => ({ riskAssessments, applyCurrentDefault })),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(({ riskAssessments, applyCurrentDefault }) =>
                this.applyRiskAssessmentOptions(riskAssessments, applyCurrentDefault),
            );
    }

    // DASH-2718 item 6: when the Site changes, the previously-picked RA may not belong to the new site, so clear it
    // and push the new site through the reload pipeline. Item 7: a user Site change defaults the fresh selection to
    // the new site's Current RA. RA is optional, so a null selection is a valid resting state.
    // updateFieldVisibility() is intentionally NOT called here — the template already invokes it right after onChange.
    private onSiteChange(): void {
        this.entity.referenceRiskAssessmentId = null;
        this.siteChange$.next({
            siteId: this.entity?.organizationLevelId ?? null,
            applyCurrentDefault: true,
        });
    }

    // Resolves the RA records scoped to the given Site (RA.organizationLevelId == siteId) using the same
    // OperatorTypeEnum filterBy shape the dialog builds for org levels. DASH-2718 item 7: reads FULL records via
    // GetList (not GetAll's id/name-only Lookup[]) so the caller can read `current` and render period/status. No
    // Site → empty list. Degrades to an empty list on error so the rest of the form stays usable (RA is optional).
    // On the edit path, a pinned RA that falls outside the current site scope (legacy plans created before the picker
    // was scoped) is unioned back in so the field renders it instead of appearing blank and dropping it on save.
    private loadScopedRiskAssessments(siteId: string | null): Observable<RiskAssessment[]> {
        if (!siteId) {
            return of([]);
        }
        return this.riskAssessmentService
            .get('', nameOf<RiskAssessment>(x => x.name), 'asc', 0, SamplingPlanDialogComponent.RA_PAGE_SIZE, this.buildSiteRaFilter(siteId))
            .pipe(
                map(result => {
                    // GetList (GetGridResponse) catches server-side errors and returns HTTP 200 with
                    // success:false + empty Data, so a BE failure would otherwise degrade to a *silent*
                    // empty picker. Route it through the shared catchError → toast + empty-list path.
                    if (result && result.success === false) {
                        throw result;
                    }
                    return result?.data ?? [];
                }),
                switchMap(scoped => this.ensurePinnedRiskAssessmentPresent(scoped)),
                catchError(error => {
                    this.toastService.showError(error);
                    return of([]);
                }),
            );
    }

    // If the plan already pins an RA that the scoped fetch excludes, fetch it by id and prepend it so the option is
    // visible. After a Site change referenceRiskAssessmentId is cleared first, so this is a no-op except on the
    // initial edit load. A failed by-id fetch degrades to the scoped list unchanged.
    private ensurePinnedRiskAssessmentPresent(scoped: RiskAssessment[]): Observable<RiskAssessment[]> {
        const pinnedId = this.entity?.referenceRiskAssessmentId;
        if (!pinnedId || scoped.some(ra => ra.id === pinnedId)) {
            return of(scoped);
        }
        return this.riskAssessmentService.getById(pinnedId).pipe(
            map(ra => (ra ? [ra, ...scoped] : scoped)),
            catchError(() => of(scoped)),
        );
    }

    // Maps the scoped RA records into picker options (name / period / status label) and, when nothing is pinned and
    // this change should default (create or a user Site change), selects the site's Current RA. Never overrides an
    // existing pick, so it can't silently change a plan the user already scoped.
    private applyRiskAssessmentOptions(riskAssessments: RiskAssessment[], applyCurrentDefault: boolean): void {
        this.riskAssessments = riskAssessments.map(ra => this.toRiskAssessmentOption(ra));
        const raField = this.fields?.find(
            f => f.property === nameOf<SamplingPlan>(x => x.referenceRiskAssessmentId),
        );
        if (raField) raField.options = this.riskAssessments;
        if (applyCurrentDefault && this.entity && this.entity.referenceRiskAssessmentId == null) {
            const current = riskAssessments.find(ra => ra.current);
            if (current) {
                this.entity.referenceRiskAssessmentId = current.id;
            }
        }
    }

    // Builds an option label showing name / period / status, e.g. "Red Dog 2027 (01/01/2027 - 31/12/2027) · Approved".
    // Period and status are dropped from the label when unavailable rather than rendering blanks/dashes.
    private toRiskAssessmentOption(ra: RiskAssessment): ILookup {
        const parts: string[] = [ra.name];
        const period = this.formatPeriod(ra.periodStart, ra.periodEnd);
        if (period) {
            parts[0] = `${ra.name} (${period})`;
        }
        const status = this.formatStatus(ra.assessmentStatus);
        if (status) {
            parts.push(status);
        }
        return { id: ra.id, name: parts.join(' · ') } as ILookup;
    }

    // Formats the RA's coverage period. Mirrors the dateFormat pipe's number|Date handling via DateFormatService and
    // the user's configured date format, so the picker reads consistently with the RA list's period column.
    private formatPeriod(start: number | Date, end: number | Date): string | null {
        const from = this.formatDate(start);
        const to = this.formatDate(end);
        if (!from && !to) {
            return null;
        }
        return `${from ?? '-'} - ${to ?? '-'}`;
    }

    private formatDate(value: number | Date | null | undefined): string | null {
        if (value === null || value === undefined) {
            return null;
        }
        const format = this.dateFormatService.userDateFormat;
        if (typeof value === 'number') {
            // PeriodStart/PeriodEnd are non-nullable ints, so the real "unset" value is 0, not null;
            // formatting it would render the epoch (01/01/1970). Treat non-positive timestamps as absent.
            return value > 0 ? this.dateFormatService.formatTimestampToString(value, format) : null;
        }
        return this.dateFormatService.formatDateTimeToString(value, format);
    }

    // The RA-level status uses AssessmentStatusEnum (Draft/InReview/Approved/Rejected). Translated so the label
    // matches the app's language; an unmapped value yields no status segment rather than a raw number.
    private formatStatus(status: AssessmentStatusEnum): string | null {
        const key = AssessmentStatusEnum[status];
        if (!key) {
            return null;
        }
        // "InReview" → "In Review" for a readable label; the key doubles as the i18n token.
        const label = key.replace(/([a-z])([A-Z])/g, '$1 $2');
        return this.translateService.getDynamicTranslation(label);
    }

    private buildSiteRaFilter(siteId: string): string {
        return JSON.stringify({
            OperatorTypeEnum: JSON.stringify({
                Condition: OperatorTypeEnum.Equal,
                Field: nameOf<RiskAssessment>(x => x.organizationLevelId),
                Value: `${siteId}`,
            }),
        });
    }

    // Mirrors SegSamplingPlanDialogComponent.validateYear exactly: returns the inline error message, or null when
    // valid. Empty is left to the `required` check so no value error shows before the user types.
    private validateYear(value: unknown): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const year = Number(value);
        if (!Number.isInteger(year) || year < 1000 || year > 9999) {
            return 'Year must be a 4-digit value';
        }
        if (year < new Date().getFullYear()) {
            return 'Year cannot be in the past';
        }
        return null;
    }

    private handleLoadError(error: unknown): void {
        this.toastService.showError(error);
        this.isFormLoading = false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
